import useHelpTarget from '../hooks/useHelpTarget';

/**
 * ScopeConfirmationPrompt
 *
 * Surfaces the output of the ``propose_query_scope`` meta-tool to
 * the user. The agent calls the tool when a question is broad
 * enough to risk an over-large answer (e.g. "show me everything"),
 * gets back per-intent facets + 2–3 suggested scopes, and the SPA
 * renders those as pickable cards.
 *
 * Click semantics:
 *
 *   * If the suggestion carries a ``topic``, we send it back as a
 *     follow-up message — the agent re-runs scoped to that topic.
 *   * If ``topic`` is null (free-form prompts like "tell me a date
 *     range"), we send the label itself, which lets the LLM
 *     interpret + ask the user for the missing parameter.
 *
 * Backend is soft-governance (memory: agentic_safety_net_patterns) —
 * the agent does NOT pause for user input. This prompt is therefore
 * a *post-answer redirect*: shown alongside follow-up suggestions
 * after the run finalises so the user can refine without retyping.
 *
 * Props:
 *   scope   — {intent_type, total_items, facets, suggested_scopes}
 *   onPick  — callback(text) — text to send as a follow-up message
 *   onDismiss — callback()
 */

const INTENT_LABEL = {
  timeline: 'Building a timeline',
  facts_about: 'Pulling facts',
  contradictions: 'Looking for contradictions',
  entities: 'Surveying entities',
};

function _summariseFacets(scope) {
  const f = scope?.facets || {};
  const bits = [];
  if (Array.isArray(f.event_types) && f.event_types.length) {
    const top = f.event_types.slice(0, 3)
      .map(x => `${x.event_type} (${x.count})`)
      .join(', ');
    bits.push(`event types: ${top}`);
  }
  if (Array.isArray(f.claim_types) && f.claim_types.length) {
    const top = f.claim_types.slice(0, 3)
      .map(x => `${x.claim_type} (${x.count})`)
      .join(', ');
    bits.push(`claim types: ${top}`);
  }
  if (Array.isArray(f.entity_types) && f.entity_types.length) {
    const top = f.entity_types.slice(0, 3)
      .map(x => `${x.entity_type} (${x.count})`)
      .join(', ');
    bits.push(`entity types: ${top}`);
  }
  return bits.join(' · ');
}

export default function ScopeConfirmationPrompt({ scope, onPick, onDismiss }) {
  const helpRef = useHelpTarget({
    slug: 'when-arc-asks-to-narrow',
    label: 'Refine the question card',
    question: 'Why is Arc asking me to narrow the question?',
  });
  if (!scope) return null;
  const scopes = Array.isArray(scope.suggested_scopes) ? scope.suggested_scopes : [];
  if (scopes.length === 0) return null;

  const heading = INTENT_LABEL[scope.intent_type] || 'Refine the question';
  const summary = _summariseFacets(scope);

  return (
    <div className="scope-prompt" role="region" aria-label="Refine query scope" ref={helpRef}>
      <div className="scope-prompt__header">
        <span className="scope-prompt__heading">
          {heading}
          {typeof scope.total_items === 'number' && (
            <span className="scope-prompt__total"> · {scope.total_items} total</span>
          )}
        </span>
        {onDismiss && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={onDismiss}
            aria-label="Dismiss scope prompt"
          >
            ×
          </button>
        )}
      </div>
      {summary && <div className="scope-prompt__summary">{summary}</div>}
      <div className="scope-prompt__cards">
        {scopes.map((s, i) => (
          <button
            key={i}
            type="button"
            className="scope-prompt__card"
            onClick={() => onPick(s.topic || s.label)}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
